import dayjs from "dayjs";
import { Icon } from "@sellar-io/components";
import { twMerge } from "tailwind-merge";
import type { UnifiedCheckoutDeliveryGroup } from "../hooks/useUnifiedCheckoutSuccessDetails";

export const UnifiedCheckoutDeliveryDetails = ({
  contactName,
  contactPhone,
  deliveryGroups,
  deliveryPreference,
  timePreference,
}: {
  contactName: string;
  contactPhone: string;
  deliveryGroups: UnifiedCheckoutDeliveryGroup[];
  deliveryPreference: "split" | "consolidated" | null;
  timePreference: string;
}) => (
  <section className="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm">
    <div className="flex items-center justify-between border-b border-zinc-200 p-4">
      <h2 className="flex items-center gap-2 font-semibold">
        <Icon icon="TRUCK_OUTLINE" size={0.8} className="text-purple-700" />
        Delivery details
      </h2>
      {deliveryGroups.flatMap((group) => group.suppliers).length > 1 && (
        <span
          className={twMerge(
            "rounded-full px-2 py-1 text-xs font-medium",
            deliveryPreference === "split"
              ? "bg-orange-100 text-orange-800"
              : "bg-purple-100 text-purple-700",
          )}
        >
          {deliveryPreference === "split"
            ? "Split delivery"
            : "Consolidated delivery"}
        </span>
      )}
    </div>
    <div className="flex flex-col gap-4 p-4">
      {deliveryGroups.map(({ date, suppliers }, index) => (
        <div key={date} className="flex flex-col gap-3">
          {deliveryPreference === "split" && (
            <p className="text-sm font-semibold">Delivery {index + 1}</p>
          )}
          <div className="divide-y-gray-200 flex flex-col divide-y rounded-lg bg-gray-50 text-base">
            <DetailRow label="Date" value={dayjs(date).format("dddd, D MMM")} />
            <DetailRow label="Time preference" value={timePreference} />
            <DetailRow label="Suppliers" value={suppliers.join(", ")} />
          </div>
        </div>
      ))}
      <div className="flex items-center gap-3 border-t border-zinc-200 pt-3 text-sm">
        <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-zinc-100 text-zinc-700">
          <Icon icon="PHONE" size={0.75} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-medium">{contactName}</p>
          <p className="text-zinc-600">{contactPhone}</p>
        </div>
        <span className="text-xs text-zinc-500">Delivery contact</span>
      </div>
    </div>
  </section>
);

const DetailRow = ({ label, value }: { label: string; value: string }) => (
  <div className="flex justify-between gap-1 px-4 py-3">
    <span className="text-zinc-600">{label}</span>
    <span className="font-semibold">{value}</span>
  </div>
);
